import React,{ ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { renderAdminTitle } from "src/admin/common/Title";
import { querySaml2ServiceProvidersAsync } from "src/api";
import { Saml2ServiceProviderDetailsDrawer, Saml2ServiceProviderFormDrawer } from "src/components";
import { CreateButtonProps, DataList, DataListColumn, DataListFetchHandler } from "src/components/DataList";
import { localize } from "src/l10n";
import { Breadcrumbs, PageHeader, UnstyledButton } from "src/ui";
import { SamlServiceProviderListViewState } from "./SamlServiceProviderListView.types";
import SpintrList from "src/ui/components/SpintrList/SpintrList";

const initialState: SamlServiceProviderListViewState = {
    drawerMode: "none",
} as const;

function SamlServiceProviderListView(): ReactElement {
    const location = useLocation();
    const listRef = useRef<SpintrList>(null);
    const [state, setState] = useState<SamlServiceProviderListViewState>(initialState);

    const adminTitle = useMemo(() => renderAdminTitle("SAML_SERVICE_PROVIDERS"), []);

    const breadcrumbs = useMemo(
        () => [{
            text: localize("Administration"),
            key: 2,
            link: "/admin",
        },
        {
            text: localize("SAML_SERVICE_PROVIDERS"),
            key: 3,
            link: location.pathname
        }],
        [location.pathname]);

    const columns = useMemo<DataListColumn[]>(
        () => [{
            key: "name",
            name: localize("NAME"),
            fieldName: "name",
            minWidth: 100,
            isSortable: false,
            onRender: (item: Spintr.Saml2ServiceProvider) => (
                <UnstyledButton
                    onClick={() => setState((s) => ({ ...s,
                        drawerMode: "details",
                        selectedSamlServiceProviderId: item.id,
                    }))}
                >
                    {item.name}
                </UnstyledButton>
            ),
        }, {
            key: "entityId",
            name: localize("ENTITY_ID"),
            fieldName: "entityId",
            minWidth: 100,
            isSortable: false,
        }],
        [setState]);

    const onListFetching = useCallback<DataListFetchHandler>(
        async (skip, take, _, __, ___, cancelToken) => {
            try {
                const queryEnvelope = await querySaml2ServiceProvidersAsync(skip, take, cancelToken);

                return {
                    total: queryEnvelope.totalCount,
                    data: queryEnvelope.items,
                };
            } catch (error) {
                console.error(error);

                return {
                    total: 0,
                    data: [],
                };
            }
        },
        []);

    const onCreateClicked = useCallback(
        () => setState({ ...state,
            drawerMode: "form",
            selectedSamlServiceProviderId: undefined,
        }),
        [state]);

    const createButtonProps = useMemo<CreateButtonProps>(
        () => ({
            iconName: "Add",
            onClick: onCreateClicked,
            text: localize("CREATE_SAML_SERVICE_PROVIDER"),
        }),
        [onCreateClicked]);

    const onFormDrawerClosing = useCallback(
        () => {
            setState({ ...state, drawerMode: "none", selectedSamlServiceProviderId: undefined });

            if (!listRef.current) {
                return;
            }

            listRef.current.reFetch();
        },
        [listRef, setState]);

    return (
        <div id="SamlServiceProviderListView">
            {adminTitle}

            <Breadcrumbs
                displayInstance={true}
                items={breadcrumbs}
                useGlobalBreadcrumbs={true} />

            <PageHeader
                title={localize("SAML_SERVICE_PROVIDERS")}
                useShowMoreSubText={true} />

            <DataList
                columns={columns}
                createButton={createButtonProps}
                fetch={onListFetching}
                orderByColumn="name"
                ref={listRef} />

            <Saml2ServiceProviderFormDrawer
                onClose={onFormDrawerClosing}
                open={state.drawerMode === "form"}
                serviceProviderId={state.selectedSamlServiceProviderId} />

            <Saml2ServiceProviderDetailsDrawer
                onClose={() => setState({ ...state, drawerMode: "none", selectedSamlServiceProviderId: undefined })}
                open={state.drawerMode === "details" && state.selectedSamlServiceProviderId !== undefined}
                serviceProviderId={state.selectedSamlServiceProviderId} />
        </div>
    )
}

export default SamlServiceProviderListView;